import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { RichTextEditor, RichTextDisplay } from '@/components/ui/RichTextEditor';
import {
  CheckIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import type {
  CorrectiveAction,
  CreateCorrectiveActionData,
  UpdateCorrectiveActionData,
} from '@/services/executive-protection.service';

// ---- Types ----

export interface AarFormData {
  // Legacy fields
  aarSummary: string;
  aarTimeline: string;
  aarRecommendations: string;
  // New structured fields
  aarPositives: string;
  aarNegatives: string;
  // Signature fields
  aarPreparedByName: string;
  aarPreparedByTitle: string;
  aarPreparedAt: string;
  aarApprovedByName: string;
  aarApprovedByTitle: string;
  aarApprovedAt: string;
}

interface AfterActionReportFormProps {
  // Legacy fields
  aarSummary?: string;
  aarTimeline?: string;
  aarRecommendations?: string;
  // New structured fields
  aarPositives?: string;
  aarNegatives?: string;
  // Signature fields
  aarPreparedByName?: string;
  aarPreparedByTitle?: string;
  aarPreparedAt?: string;
  aarApprovedByName?: string;
  aarApprovedByTitle?: string;
  aarApprovedAt?: string;
  // Metadata
  aarCompletedAt?: string;
  aarCompletedBy?: string;
  // Corrective actions
  correctiveActions: CorrectiveAction[];
  onCreateCorrectiveAction: (data: CreateCorrectiveActionData) => Promise<void>;
  onUpdateCorrectiveAction: (actionId: string, data: UpdateCorrectiveActionData) => Promise<void>;
  onDeleteCorrectiveAction: (actionId: string) => Promise<void>;
  // State
  readOnly: boolean;
  hasLegacyData: boolean;
  onSaveDraft: (data: AarFormData) => Promise<void>;
  onComplete: (data: AarFormData) => Promise<void>;
  isSaving?: boolean;
}

// ---- Corrective Actions Table ----

function CorrectiveActionsTable({
  actions,
  readOnly,
  onCreate,
  onUpdate,
  onDelete,
}: {
  actions: CorrectiveAction[];
  readOnly: boolean;
  onCreate: (data: CreateCorrectiveActionData) => Promise<void>;
  onUpdate: (actionId: string, data: UpdateCorrectiveActionData) => Promise<void>;
  onDelete: (actionId: string) => Promise<void>;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateCorrectiveActionData>({
    description: '',
    assignedTo: '',
    priority: 'MEDIUM',
    dueDate: '',
    status: 'OPEN',
  });

  const resetForm = () => {
    setFormData({ description: '', assignedTo: '', priority: 'MEDIUM', dueDate: '', status: 'OPEN' });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!formData.description.trim()) {
      return;
    }
    if (editingId) {
      await onUpdate(editingId, formData);
    } else {
      await onCreate(formData);
    }
    resetForm();
  };

  const startEdit = (action: CorrectiveAction) => {
    setEditingId(action.id);
    setFormData({
      description: action.description,
      assignedTo: action.assignedTo ?? '',
      priority: action.priority,
      dueDate: action.dueDate ? action.dueDate.split('T')[0] : '',
      status: action.status,
    });
    setIsAdding(true);
  };

  const priorityColors: Record<string, string> = {
    HIGH: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    LOW: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };

  const statusColors: Record<string, string> = {
    OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    IN_PROGRESS: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    CLOSED: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-200',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300">
          Section 3 — Corrective Actions
        </label>
        {!readOnly && !isAdding && (
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Action
          </Button>
        )}
      </div>

      {isAdding && !readOnly && (
        <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 mb-4 space-y-3 bg-secondary-50 dark:bg-secondary-800">
          <div>
            <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Action Item *</label>
            <input
              type="text"
              className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 text-sm"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the corrective action..."
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Assigned To</label>
              <input
                type="text"
                className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 text-sm"
                value={formData.assignedTo ?? ''}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                placeholder="Name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Priority</label>
              <select
                className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 text-sm"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'HIGH' | 'MEDIUM' | 'LOW' })}
              >
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div>
              <DateInput
                label="Due Date"
                value={formData.dueDate ?? ''}
                onChange={(v) => setFormData({ ...formData, dueDate: v })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">Status</label>
              <select
                className="w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 text-sm"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'OPEN' | 'IN_PROGRESS' | 'CLOSED' })}
              >
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={!formData.description.trim()}>
              {editingId ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      )}

      {actions.length === 0 && !isAdding ? (
        <p className="text-sm text-secondary-400 dark:text-secondary-500 italic">No corrective actions added yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-200 dark:border-secondary-700">
                <th className="text-left py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Action Item</th>
                <th className="text-left py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Assigned To</th>
                <th className="text-left py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Priority</th>
                <th className="text-left py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Due Date</th>
                <th className="text-left py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Status</th>
                {!readOnly && <th className="text-right py-2 px-2 font-medium text-secondary-600 dark:text-secondary-400">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {actions.map((action) => (
                <tr key={action.id} className="border-b border-secondary-100 dark:border-secondary-800">
                  <td className="py-2 px-2 text-secondary-900 dark:text-secondary-100">{action.description}</td>
                  <td className="py-2 px-2 text-secondary-700 dark:text-secondary-300">{action.assignedTo ?? '—'}</td>
                  <td className="py-2 px-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${priorityColors[action.priority] || ''}`}>
                      {action.priority}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-secondary-700 dark:text-secondary-300">
                    {action.dueDate ? new Date(action.dueDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 px-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusColors[action.status] || ''}`}>
                      {action.status.replace('_', ' ')}
                    </span>
                  </td>
                  {!readOnly && (
                    <td className="py-2 px-2 text-right">
                      <button
                        className="p-1 text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300"
                        onClick={() => startEdit(action)}
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1 text-red-500 hover:text-red-700 ml-1"
                        onClick={() => onDelete(action.id)}
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Signature Section ----

function SignatureSection({
  preparedByName,
  preparedByTitle,
  preparedAt,
  approvedByName,
  approvedByTitle,
  approvedAt,
  readOnly,
  onChange,
}: {
  preparedByName: string;
  preparedByTitle: string;
  preparedAt: string;
  approvedByName: string;
  approvedByTitle: string;
  approvedAt: string;
  readOnly: boolean;
  onChange: (field: string, value: string) => void;
}) {
  const inputClass = readOnly
    ? 'w-full bg-secondary-50 dark:bg-secondary-800 rounded px-3 py-2 text-sm text-secondary-700 dark:text-secondary-300'
    : 'w-full rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 text-sm';

  return (
    <div>
      <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-3">
        Signatures & Approval
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Prepared By */}
        <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-secondary-800 dark:text-secondary-200">Prepared By {!readOnly && <span className="text-red-500">*</span>}</h4>
          <div>
            <label className="block text-xs text-secondary-500 dark:text-secondary-400 mb-1">Name</label>
            <input
              type="text"
              className={inputClass}
              value={preparedByName}
              onChange={(e) => onChange('aarPreparedByName', e.target.value)}
              readOnly={readOnly}
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="block text-xs text-secondary-500 dark:text-secondary-400 mb-1">Title</label>
            <input
              type="text"
              className={inputClass}
              value={preparedByTitle}
              onChange={(e) => onChange('aarPreparedByTitle', e.target.value)}
              readOnly={readOnly}
              placeholder="Job title"
            />
          </div>
          <div>
            <DateInput
              label="Date"
              value={preparedAt}
              onChange={(v) => onChange('aarPreparedAt', v)}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* Reviewed & Approved By */}
        <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-secondary-800 dark:text-secondary-200">Reviewed & Approved By {!readOnly && <span className="text-red-500">*</span>}</h4>
          <div>
            <label className="block text-xs text-secondary-500 dark:text-secondary-400 mb-1">Name</label>
            <input
              type="text"
              className={inputClass}
              value={approvedByName}
              onChange={(e) => onChange('aarApprovedByName', e.target.value)}
              readOnly={readOnly}
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="block text-xs text-secondary-500 dark:text-secondary-400 mb-1">Title</label>
            <input
              type="text"
              className={inputClass}
              value={approvedByTitle}
              onChange={(e) => onChange('aarApprovedByTitle', e.target.value)}
              readOnly={readOnly}
              placeholder="Job title"
            />
          </div>
          <div>
            <DateInput
              label="Date"
              value={approvedAt}
              onChange={(v) => onChange('aarApprovedAt', v)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Legacy AAR Display ----

function LegacyAarDisplay({
  aarSummary,
  aarTimeline,
  aarRecommendations,
}: {
  aarSummary?: string;
  aarTimeline?: string;
  aarRecommendations?: string;
}) {
  return (
    <div className="space-y-4 border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20">
      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">
        Legacy AAR Data (Read Only)
      </p>
      <div>
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
          Incident Summary
        </label>
        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4">
          <RichTextDisplay content={aarSummary ?? 'No summary provided.'} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
          Timeline of Events
        </label>
        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4">
          <RichTextDisplay content={aarTimeline ?? 'No timeline provided.'} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
          Recommendations
        </label>
        <div className="bg-white dark:bg-secondary-800 rounded-lg p-4">
          <RichTextDisplay content={aarRecommendations ?? 'No recommendations provided.'} />
        </div>
      </div>
    </div>
  );
}

// ---- Main Form ----

export default function AfterActionReportForm({
  aarSummary = '',
  aarTimeline = '',
  aarRecommendations = '',
  aarPositives: initialPositives = '',
  aarNegatives: initialNegatives = '',
  aarPreparedByName: initialPrepName = '',
  aarPreparedByTitle: initialPrepTitle = '',
  aarPreparedAt: initialPrepAt = '',
  aarApprovedByName: initialApproveName = '',
  aarApprovedByTitle: initialApproveTitle = '',
  aarApprovedAt: initialApproveAt = '',
  aarCompletedAt,
  aarCompletedBy,
  correctiveActions,
  onCreateCorrectiveAction,
  onUpdateCorrectiveAction,
  onDeleteCorrectiveAction,
  readOnly,
  hasLegacyData,
  onSaveDraft,
  onComplete,
  isSaving = false,
}: AfterActionReportFormProps) {
  const [positives, setPositives] = useState(initialPositives ?? '');
  const [negatives, setNegatives] = useState(initialNegatives ?? '');
  const [preparedByName, setPreparedByName] = useState(initialPrepName ?? '');
  const [preparedByTitle, setPreparedByTitle] = useState(initialPrepTitle ?? '');
  const [preparedAt, setPreparedAt] = useState(initialPrepAt ? initialPrepAt.split('T')[0] : '');
  const [approvedByName, setApprovedByName] = useState(initialApproveName ?? '');
  const [approvedByTitle, setApprovedByTitle] = useState(initialApproveTitle ?? '');
  const [approvedAt, setApprovedAt] = useState(initialApproveAt ? initialApproveAt.split('T')[0] : '');
  const [errors, setErrors] = useState<string[]>([]);

  // Sync state when props change (e.g., after save) — coalesce nulls to empty strings
  useEffect(() => { setPositives(initialPositives ?? ''); }, [initialPositives]);
  useEffect(() => { setNegatives(initialNegatives ?? ''); }, [initialNegatives]);
  useEffect(() => { setPreparedByName(initialPrepName ?? ''); }, [initialPrepName]);
  useEffect(() => { setPreparedByTitle(initialPrepTitle ?? ''); }, [initialPrepTitle]);
  useEffect(() => { setPreparedAt(initialPrepAt ? initialPrepAt.split('T')[0] : ''); }, [initialPrepAt]);
  useEffect(() => { setApprovedByName(initialApproveName ?? ''); }, [initialApproveName]);
  useEffect(() => { setApprovedByTitle(initialApproveTitle ?? ''); }, [initialApproveTitle]);
  useEffect(() => { setApprovedAt(initialApproveAt ? initialApproveAt.split('T')[0] : ''); }, [initialApproveAt]);

  const getFormData = (): AarFormData => ({
    aarSummary: aarSummary ?? '',
    aarTimeline: aarTimeline ?? '',
    aarRecommendations: aarRecommendations ?? '',
    aarPositives: positives,
    aarNegatives: negatives,
    aarPreparedByName: preparedByName,
    aarPreparedByTitle: preparedByTitle,
    aarPreparedAt: preparedAt || '',
    aarApprovedByName: approvedByName,
    aarApprovedByTitle: approvedByTitle,
    aarApprovedAt: approvedAt || '',
  });

  const handleSignatureChange = (field: string, value: string) => {
    switch (field) {
      case 'aarPreparedByName': setPreparedByName(value); break;
      case 'aarPreparedByTitle': setPreparedByTitle(value); break;
      case 'aarPreparedAt': setPreparedAt(value); break;
      case 'aarApprovedByName': setApprovedByName(value); break;
      case 'aarApprovedByTitle': setApprovedByTitle(value); break;
      case 'aarApprovedAt': setApprovedAt(value); break;
    }
  };

  const handleSaveDraft = async () => {
    setErrors([]);
    await onSaveDraft(getFormData());
  };

  const handleComplete = async () => {
    const validationErrors: string[] = [];

    const hasPositives = positives && positives.trim() !== '' && positives.trim() !== '<p></p>';
    const hasNegatives = negatives && negatives.trim() !== '' && negatives.trim() !== '<p></p>';
    if (!hasPositives && !hasNegatives) {
      validationErrors.push('At least one of Positives or Negatives must be filled.');
    }
    if (!preparedByName.trim()) {
      validationErrors.push('Preparer name is required.');
    }
    if (!approvedByName.trim()) {
      validationErrors.push('Approver name is required.');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    await onComplete(getFormData());
  };

  // Read-only mode
  if (readOnly) {
    return (
      <div className="space-y-6">
        {/* Show legacy data if present */}
        {hasLegacyData && (
          <LegacyAarDisplay
            aarSummary={aarSummary}
            aarTimeline={aarTimeline}
            aarRecommendations={aarRecommendations}
          />
        )}

        {/* Section 1 — Positives */}
        <div>
          <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
            Section 1 — Positives
          </label>
          <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4">
            <RichTextDisplay content={initialPositives || 'No positives recorded.'} />
          </div>
        </div>

        {/* Section 2 — Negatives */}
        <div>
          <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
            Section 2 — Negatives
          </label>
          <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4">
            <RichTextDisplay content={initialNegatives || 'No negatives recorded.'} />
          </div>
        </div>

        {/* Section 3 — Corrective Actions */}
        <CorrectiveActionsTable
          actions={correctiveActions}
          readOnly={true}
          onCreate={onCreateCorrectiveAction}
          onUpdate={onUpdateCorrectiveAction}
          onDelete={onDeleteCorrectiveAction}
        />

        {/* Signatures */}
        <SignatureSection
          preparedByName={initialPrepName}
          preparedByTitle={initialPrepTitle}
          preparedAt={initialPrepAt ? initialPrepAt.split('T')[0] : ''}
          approvedByName={initialApproveName}
          approvedByTitle={initialApproveTitle}
          approvedAt={initialApproveAt ? initialApproveAt.split('T')[0] : ''}
          readOnly={true}
          onChange={() => {/* noop */}}
        />

        {aarCompletedAt && (
          <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4 mt-4">
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Completed {aarCompletedBy ? `by ${aarCompletedBy}` : ''} on{' '}
              {new Date(aarCompletedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Editable mode
  return (
    <div className="space-y-6">
      {/* Show legacy data read-only if present */}
      {hasLegacyData && (
        <LegacyAarDisplay
          aarSummary={aarSummary}
          aarTimeline={aarTimeline}
          aarRecommendations={aarRecommendations}
        />
      )}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Section 1 — Positives */}
      <div>
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
          Section 1 — Positives
        </label>
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
          Effective tactics, strong performance, standout actions.
        </p>
        <RichTextEditor value={positives} onChange={setPositives} />
      </div>

      {/* Section 2 — Negatives */}
      <div>
        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
          Section 2 — Negatives
        </label>
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
          Breakdowns, errors, missed protocols.
        </p>
        <RichTextEditor value={negatives} onChange={setNegatives} />
      </div>

      {/* Section 3 — Corrective Actions */}
      <CorrectiveActionsTable
        actions={correctiveActions}
        readOnly={false}
        onCreate={onCreateCorrectiveAction}
        onUpdate={onUpdateCorrectiveAction}
        onDelete={onDeleteCorrectiveAction}
      />

      {/* Signatures & Approval */}
      <SignatureSection
        preparedByName={preparedByName}
        preparedByTitle={preparedByTitle}
        preparedAt={preparedAt}
        approvedByName={approvedByName}
        approvedByTitle={approvedByTitle}
        approvedAt={approvedAt}
        readOnly={false}
        onChange={handleSignatureChange}
      />

      {/* Action buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t border-secondary-200 dark:border-secondary-700">
        <Button variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
          <DocumentTextIcon className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button onClick={handleComplete} disabled={isSaving}>
          <CheckIcon className="w-4 h-4 mr-2" />
          Complete AAR & Close Activity
        </Button>
      </div>
    </div>
  );
}
